import { useCallback, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import {
  useKioskSession,
  useKioskSessionLocale
} from "~/stores/kiosk-session/hooks/use-kiosk-session";
import { kioskActions } from "~/stores/kiosk-session/actions";
import { useAbortTransaction } from "~/hooks/queries/terminals";
import { priceFormatter } from "~/utils/price-formatter";
import { useKioskProducts } from "~/stores/kiosk-session/hooks/use-kiosk-products";
import {
  CardPaymentModal,
  DefaultPaymentModal,
  SwishPaymentModal
} from "~/components/kiosk/ui/modals/checkout/payment";
import { ModalLoadingGeneric } from "~/components/kiosk/ui/modals/loading/modal-loading-generic";
import { ModalError } from "~/components/kiosk/ui/modals/error/modal-error";
import { ModalOutOfStockContainer } from "~/components/kiosk/ui/modals/out-of-stock/modal-out-of-stock-container";
import { ModalOrderMinimumContainer } from "~/components/kiosk/ui/modals/order-minimum/modal-order-minimum-container";
import { DisplayErrorMessage } from "~/components/display-error-message";
import { ApiErrorCodesEnum, isApiErrorCode } from "~/api/error";
import { useKioskCheckoutStatus } from "~/components/kiosk/payment-buttons/use-kiosk-checkout-status";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";

export const KioskBasketPaymentPage = () => {
  const navigate = useNavigate();
  const returnToBasket = useCallback(() => {
    kioskActions.session.resetOrderId();
    kioskActions.session.generateServiceId();
    navigate("/kiosk/basket");
  }, [navigate]);

  const locale = useKioskSessionLocale();
  const { unitTotalPayableInclVat, currency } = useKioskProducts();

  const { data: till } = useCurrentTill();

  const hasReceiptModeAutomatic =
    till?.type === "kiosk" && till?.config.receiptPrintMode === "automatic";

  const orderId = useKioskSession((session) => session?.orderId);
  const serviceId = useKioskSession((session) => session?.serviceId);

  const { status, error, paymentMethod } = useKioskCheckoutStatus();
  const isTerminalPayment =
    paymentMethod === "card" || paymentMethod === "swish";

  const { mutate: abortTransaction, status: abortTransactionStatus } =
    useAbortTransaction();

  // The order mutation is created before this route is entered, so `idle`
  // means it is gone: garbage collected under an unattended error modal, or
  // lost to a reload. Nothing is in flight, so the overlay must not wait.
  useEffect(() => {
    if (status === "idle") {
      returnToBasket();
    }
  }, [status, returnToBasket]);

  if (!orderId || !serviceId) {
    return <Navigate to="/kiosk/basket" />;
  }

  if (
    status === "idle" ||
    abortTransactionStatus === "pending" ||
    (status === "pending" && !isTerminalPayment)
  ) {
    return <ModalLoadingGeneric />;
  }

  if (status === "error") {
    if (isApiErrorCode(error, ApiErrorCodesEnum.OFFER_PRODUCTS_NOT_AVAILABLE)) {
      // Adyen captures then reverses on OOS; credit and billing orders never
      // reach the terminal and FreedomPay never captures, so gate the
      // reversal note.
      return (
        <ModalOutOfStockContainer
          error={error}
          onBack={returnToBasket}
          paymentReversed={isTerminalPayment}
        />
      );
    }

    if (
      isApiErrorCode(
        error,
        ApiErrorCodesEnum.ORDER_VALIDATION_MINIMUM_AMOUNT_NOT_REACHED
      )
    ) {
      return (
        <ModalOrderMinimumContainer
          error={error}
          onBack={returnToBasket}
          paymentReversed={isTerminalPayment}
        />
      );
    }

    return (
      <ModalError onClose={returnToBasket}>
        <DisplayErrorMessage error={error} />
      </ModalError>
    );
  }

  if (status === "success") {
    if (hasReceiptModeAutomatic) {
      return <Navigate to="/kiosk/order-confirmation/print" />;
    }
    return <Navigate to="/kiosk/order-confirmation" />;
  }

  const formattedTotalAmount = priceFormatter({
    value: unitTotalPayableInclVat,
    currency,
    locale
  });

  const handleAbortTransaction = () => {
    abortTransaction({ orderId, serviceId }, { onSettled: returnToBasket });
  };

  if (paymentMethod === "swish") {
    return (
      <SwishPaymentModal
        price={formattedTotalAmount}
        onCancel={handleAbortTransaction}
      />
    );
  }

  if (paymentMethod === "card") {
    return (
      <CardPaymentModal
        price={formattedTotalAmount}
        onCancel={handleAbortTransaction}
      />
    );
  }

  return (
    <DefaultPaymentModal
      price={formattedTotalAmount}
      onCancel={handleAbortTransaction}
    />
  );
};
